import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import './QrScannerModal.css';

interface Props {
  onClose: () => void;
  onScan: (qrCode: string) => void;
}

type BarcodeDetectorType = {
  detect: (src: HTMLCanvasElement) => Promise<{ rawValue: string }[]>;
};

export default function QrScannerModal({ onClose, onScan }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animRef = useRef<number | null>(null);
  const detectorRef = useRef<BarcodeDetectorType | null>(null);
  const scannedRef = useRef(false);
  const mountedRef = useRef(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    startCamera();
    return () => {
      mountedRef.current = false;
      if (animRef.current) cancelAnimationFrame(animRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    try {
      // Instantiate BarcodeDetector once — reuse across all frames
      if ('BarcodeDetector' in window) {
        try {
          detectorRef.current = new (window as unknown as {
            BarcodeDetector: new (o: { formats: string[] }) => BarcodeDetectorType;
          }).BarcodeDetector({ formats: ['qr_code'] });
        } catch { /* unsupported, will fall back to jsqr */ }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
      animRef.current = requestAnimationFrame(scanFrame);
    } catch {
      setError('Нет доступа к камере. Разреши доступ в настройках.');
    }
  };

  const parseQrValue = (raw: string): string | null => {
    // ~ is valid in OLSO QR codes (used as delimiter in auth token scheme)
    const match = raw.match(/\/order\/([A-Za-z0-9~_-]+)/);
    if (match) return match[1];
    try {
      const parsed = new URL(raw);
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (segments.length > 0) {
        const last = segments[segments.length - 1];
        if (/^[A-Za-z0-9~_-]{4,}$/.test(last)) return last;
      }
    } catch { /* not a URL */ }
    if (/^[A-Za-z0-9~_-]{4,}$/.test(raw)) return raw;
    return null;
  };

  const scanFrame = async () => {
    if (scannedRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      animRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) { animRef.current = requestAnimationFrame(scanFrame); return; }
    ctx.drawImage(video, 0, 0);

    let rawValue: string | null = null;

    // Primary: reuse pre-created BarcodeDetector
    if (detectorRef.current) {
      try {
        const results = await detectorRef.current.detect(canvas);
        if (results.length > 0) rawValue = results[0].rawValue;
      } catch { /* detect failed on this frame */ }
    }

    // Fallback: jsQR (static import — always available)
    if (!rawValue) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(imageData.data, imageData.width, imageData.height);
      if (result) rawValue = result.data;
    }

    if (rawValue) {
      const code = parseQrValue(rawValue);
      if (code) {
        scannedRef.current = true;
        navigator.vibrate?.(80);
        // Do NOT call stopCamera() here — rely on useEffect cleanup on unmount
        onScan(code); // triggers setShowScanner(false) → unmount → cleanup runs
        return;
      }
    }

    animRef.current = requestAnimationFrame(scanFrame);
  };

  return (
    <div className="qr-overlay" onClick={onClose}>
      <div className="qr-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="qr-close" onClick={onClose} aria-label="Закрыть">×</button>

        {error ? (
          <div className="qr-error">
            <div className="qr-error-icon"></div>
            <div className="qr-error-text">{error}</div>
          </div>
        ) : (
          <>
            <video ref={videoRef} className="qr-video" playsInline muted />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="qr-frame">
              <span className="qr-corner qr-corner--tl" />
              <span className="qr-corner qr-corner--tr" />
              <span className="qr-corner qr-corner--bl" />
              <span className="qr-corner qr-corner--br" />
              <div className="qr-scan-line" />
            </div>
            <div className="qr-hint">Наведи камеру на QR-код заказа</div>
          </>
        )}
      </div>
    </div>
  );
}
